import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";

import PhoneInput, { isValidPhoneNumber } from "react-phone-number-input";
import "react-phone-number-input/style.css";
import ReCAPTCHA from "react-google-recaptcha";
import { useNavigate } from "react-router-dom";
import { Container, Image, Form, Spinner } from "react-bootstrap";
import { useTheme } from "@mui/material";
import { tokens } from "../../../theme";
import "bootstrap/dist/css/bootstrap.min.css";
import "./Signup.css";
import "../../../components/sign-in/signin.css";

import logo2 from "../../../assets/images/logo2.png";
import responsivebg from "../../../assets/images/imge_banner_1.jpg";
import backarrow from "../../../assets/images/backarrow.jpg";

import Stepper from "../../../components/SignUp/Stepper";
import { useCookies } from "../../../hooks/useCookies";
import { useAppContext } from "../../../Context/UseContext";
import * as verificationAction from "../../../actions/verification";
import { useGeolocation, DEFAULT_COUNTRY } from "../../../utils/geolocationUtils.jsx";
import { useRateLimit } from "../../../hooks/useRateLimit";
import { Logo } from "../../../components/DesignSystem/components/Logo";
import { Heart, ChevronDown, ArrowRight, ShieldCheck, Globe, HelpCircle, ArrowLeft } from "lucide-react";

export const SignupPhone = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const recaptchaRef = useRef(null);

  const navigate = useNavigate();
  const { setCookie } = useCookies();
  const { detectCountry } = useGeolocation();
  const { phoneNumber, setPhoneNumber } = useAppContext();

  const [selectedCountry, setSelectedCountry] = useState(DEFAULT_COUNTRY);
  const [errorMessage, setErrorMessage] = useState("");
  const [isSendingOtp, setIsSendingOtp] = useState(false);

  // Rate limiting hook
  const {
    isRateLimited,
    timeRemaining,
    handleRateLimitError,
    formatTimeRemaining,
    getRateLimitMessage
  } = useRateLimit('signup');

  // Geolocation effect
  useEffect(() => {
    let isMounted = true;

    const initializeCountry = async () => {
      try {
        const detectedCountry = await detectCountry();
        if (detectedCountry && isMounted) {
          setSelectedCountry(detectedCountry);
        }
      } catch (error) {
        console.warn("Failed to initialize country detection:", error);
        // Component will use default country (CA) if geolocation fails
      }
    };

    initializeCountry();

    return () => {
      isMounted = false;
    };
  }, [detectCountry]);

  // Clear specific cookies on component mount (preserve phoneNumber if it exists)
  useEffect(() => {
    // Only clear cookies that are not needed for the signup flow
    // Preserve phoneNumber cookie in case user navigates back
    const cookiesToPreserve = ['phoneNumber'];
    const allCookies = document.cookie.split(';');

    allCookies.forEach(cookie => {
      const cookieName = cookie.split('=')[0].trim();
      if (!cookiesToPreserve.includes(cookieName)) {
        document.cookie = `${cookieName}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;`;
      }
    });
  }, []);

  const handlePhoneNumberChange = useCallback((value) => {
    setPhoneNumber(value || "");
    setErrorMessage(""); // Clear error when user starts typing
  }, [setPhoneNumber]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isSendingOtp || isRateLimited) return;

    // Validate phone number
    if (!phoneNumber || !isValidPhoneNumber(phoneNumber)) {
      setErrorMessage("*Please enter a valid phone number.");
      return;
    }

    setIsSendingOtp(true);
    setErrorMessage("");

    try {
      recaptchaRef.current.reset();
      const token = await recaptchaRef.current.executeAsync();

      // OPTIMISTIC NAVIGATION: Go to EnterCode instantly
      setCookie("phoneNumber", phoneNumber, 7);
      navigate("/entercode");

      // BACKGROUND API CALL
      verificationAction.signupOtp({
        phone: phoneNumber,
        recaptchaToken: token
      }).then((response) => {
        if (!response || response.status !== true) {
            console.error("Failed to send code in background", response);
        }
      }).catch((error) => console.error("OTP background error:", error));

    } catch (error) {
      console.error("OTP error:", error);
      setErrorMessage("*An error occurred. Please try again later.");
      setIsSendingOtp(false);
    }
  };

  const handleBackNavigation = useCallback(() => {
    window.history.back();
  }, []);

  const mode = theme.palette.mode;
  
  // Track Tailwind loading state to prevent FOUC (Flash of Unstyled Content)
  const [isTailwindLoaded, setIsTailwindLoaded] = useState(!!window.tailwind);

  useEffect(() => {
    const applyConfig = () => {
      const modeColors = tokens(mode);
      if (window.tailwind) {
        window.tailwind.config = {
          darkMode: "class",
          theme: {
              extend: {
                  colors: {
                      "primary": modeColors.greenAccent[500] || "#ff794d",
                      "background-light": mode === "dark" ? modeColors.primary[500] : "#f8f6f5",
                      "background-dark": modeColors.primary[500] || "#23140f",
                      "premium-black": mode === "dark" ? "#ffffff" : "#111111"
                  },
                  fontFamily: {
                      "display": ["Inter", "sans-serif"],
                      "body": ['"Source Sans Pro"', "sans-serif"],
                      "button": ["Poppins", "sans-serif"]
                  },
                  borderRadius: {
                      "DEFAULT": "1rem",
                      "lg": "2rem",
                      "xl": "3rem",
                      "full": "9999px"
                  },
              },
          },
        };
      }
    };

    if (!document.getElementById("tailwind-script")) {
      const script = document.createElement("script");
      script.id = "tailwind-script";
      script.src = "https://cdn.tailwindcss.com?plugins=forms,container-queries";
      
      script.onload = () => {
        applyConfig();
        setIsTailwindLoaded(true);
      };

      document.head.appendChild(script);
      
      const link1 = document.createElement("link");
      link1.rel = "stylesheet";
      link1.href = "https://fonts.googleapis.com/css2?family=Material+Symbols+Outlined:wght@100..700,0..1&display=swap";
      document.head.appendChild(link1);
    } else {
      applyConfig();
      // Fast check if already loaded
      if (window.tailwind) setIsTailwindLoaded(true);
    }
  }, [mode]);

  return (
    <div 
      className="bg-background-light dark:bg-background-dark font-body antialiased overflow-hidden w-full m-0 p-0" 
      style={{ height: "100vh", opacity: isTailwindLoaded ? 1 : 0, transition: 'opacity 0.1s ease-out' }}
    >
      <div className="flex h-screen w-full">
        {/* Left Side: Hero Image */}
        <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden h-full">
          <div className="absolute inset-0 bg-gradient-to-t from-background-dark/30 to-transparent z-10"></div>
          <img alt="Happy couple laughing together" className="absolute inset-0 h-full w-full object-cover object-center scale-[1.15]" data-alt="Happy young couple in high contrast lighting laughing together" src={require("../../../assets/images/img_signup.png")}/>
          <div className="absolute bottom-8 left-12 z-20 max-w-md">
            <div className="flex items-center gap-2 mb-4">
               <Logo showText={false} />
               <h2 className="login-hero-title">myTamilDate</h2>                           
            </div>
          </div>
        </div>
        {/* Right Side: Auth Form */}
        <div className="w-full lg:w-1/2 flex flex-col bg-white dark:bg-background-dark px-6 lg:px-[var(--ds-space-20)] pt-[var(--ds-space-mobile-top)] pb-[var(--ds-space-12)] lg:py-[var(--ds-space-16)] h-full overflow-y-auto">
          {/* Main Auth Card */}
          <div className="flex-1 flex flex-col justify-start lg:justify-center max-w-md mx-auto w-full">
            {/* Mobile Back Button */}
            <div className="lg:hidden flex justify-start mb-[var(--ds-space-8)] -ml-2 text-slate-900 dark:text-slate-100">
                <button type="button" onClick={handleBackNavigation} className="flex items-center justify-center w-10 h-10 rounded-full bg-transparent hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors" aria-label="Go back">
                    <ArrowLeft className="shrink-0" size={24} />
                </button>
            </div>
            <div className="mb-[var(--ds-space-8)]">
              <h1 className="text-slate-900 dark:text-slate-100 text-4xl lg:text-5xl font-black font-display tracking-tight mb-4">
                What's your number?
              </h1>
              <p className="text-slate-500 dark:text-slate-400 text-lg leading-normal">
                We protect our community by making sure everyone on our app is real.
              </p>
            </div>
            {/* Input Group */}
            <form onSubmit={handleSubmit} className="flex flex-col gap-[var(--ds-space-6)]">
              <div className="flex flex-col gap-4">
                <div className="flex gap-3">
                  <div className="relative w-full">
                    <label className="block text-xs font-bold uppercase tracking-wider text-slate-400 mb-1 ml-4" htmlFor="phoneInput">Phone Number</label>
                    <PhoneInput
                      international
                      withCountryCallingCode
                      defaultCountry={selectedCountry}
                      value={phoneNumber}
                      onChange={handlePhoneNumberChange}
                      className={`premium-phone-input ${errorMessage ? 'error' : ''} ${isSendingOtp || isRateLimited ? 'disabled' : ''}`}
                      disabled={isSendingOtp || isRateLimited}
                    />
                  </div>
                </div>
              </div>
              {errorMessage && (
                <div className="text-red-500 text-sm mt-2 px-2">
                  {errorMessage}
                  {isRateLimited && timeRemaining > 0 && (
                    <div style={{ marginTop: "4px", fontWeight: "bold" }}>
                      Time remaining: {formatTimeRemaining(timeRemaining)}
                    </div>
                  )}
                </div>
              )}
              {/* Action Button */}
              <button 
                type="submit"
                disabled={isSendingOtp || isRateLimited}
                className="w-full h-14 bg-premium-black dark:bg-slate-100 text-white dark:text-premium-black rounded-full font-button font-semibold tracking-[0.06em] text-lg hover:opacity-90 transition-all flex items-center justify-center gap-2 group disabled:opacity-50 disabled:cursor-not-allowed">
                {isSendingOtp ? (
                  <Spinner animation="border" size="sm" role="status" aria-hidden="true" />
                ) : isRateLimited ? (
                  `Try again in ${formatTimeRemaining(timeRemaining)}`
                ) : (
                  <>
                    Continue
                    <ArrowRight className="group-hover:translate-x-1 transition-transform" size={24} />
                  </>
                )}
              </button>
              <ReCAPTCHA
                ref={recaptchaRef}
                sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY || "dummy_key"}
                size="invisible"
                badge="bottomright"
                style={{ visibility: 'hidden', position: 'absolute', left: '-9999px' }}
                onErrored={() => console.log("reCAPTCHA errored")}
                onExpired={() => console.log("reCAPTCHA expired")}
              />
              <div className="flex items-start gap-3 px-2">
                <ShieldCheck className="text-primary mt-0.5" size={24} />
                <p className="text-slate-400 text-xs leading-relaxed">
                  By clicking Continue, you agree to our <a className="underline hover:text-primary transition-colors" href="/Tnc" target="_blank" rel="noopener noreferrer">Terms of Service</a> and <a className="underline hover:text-primary transition-colors" href="/PrivacyPolicy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>. We will send a one-time verification code. Message and data rates may apply.
                </p>
              </div>
            </form>
          </div>
          {/* Footer / Secondary Actions */}
          <div className="mt-auto pt-[var(--ds-space-8)] border-t border-slate-100 dark:border-slate-800 flex justify-between items-center text-sm font-medium text-slate-500">
            <button className="hover:text-primary transition-colors" onClick={() => navigate("/signinoptions")}>Already have an account?</button>
            <div className="flex items-center gap-4">
              <Globe className="cursor-pointer hover:text-primary transition-colors" size={24} strokeWidth={2} />
              <HelpCircle className="cursor-pointer hover:text-primary transition-colors" size={24} strokeWidth={2} onClick={() => navigate("/faq")} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
